import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { BarChart3Icon, FileBarChartIcon, HandCoinsIcon, RecycleIcon, SearchIcon, SparklesIcon } from 'lucide-react';
import { Section } from '../Section';
import { ImageWindow } from '../ImageWindow';
import { InView } from '../InView';
import { compoundBenefits, compoundFeatures } from '../../../data/deck';
const FEATURE_ICONS: Record<string, typeof BarChart3Icon> = {
  see: BarChart3Icon,
  learn: SparklesIcon
};
const BENEFIT_ICONS: Record<string, typeof BarChart3Icon> = {
  reuse: RecycleIcon,
  spot: SearchIcon,
  buy: HandCoinsIcon
};
const PROOF_REPORT_IMAGE = 'https://cdn.magicpatterns.com/uploads/huxAYnV7xByn5y6cTZwTmW/Screenshot_2026-10-22_at_13.09.21.png';
export function CompoundSection() {
  const [proofOpen, setProofOpen] = useState(false);
  return <Section id="compound" data-id="element-393" data-mp="2qoi1nxd8955">
      <AnimatePresence data-id="element-394" data-mp="2rgd8xn1w4uw">
        {proofOpen && <ImageWindow key="proof" src={PROOF_REPORT_IMAGE} alt="Snak data report for the Seretide Kenya teaser: what needs doing and by whom, with channel and CRM-code filters" title="Proof case · Snak data report" onClose={() => setProofOpen(false)} data-id="element-395" data-mp="7oq48hq38oku" />}
      </AnimatePresence>
      <div className="grid items-end gap-8 lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-16" data-id="element-396" data-mp="1ivf388e8q6vv">
        <InView data-id="element-397" data-mp="1eu1vfsrl7tmb">
          <p className="text-[18px] font-semibold italic text-[var(--muted)]" data-id="element-398" data-mp="r6lh5u15pzed">
            “Every Snak already captures what HCPs think, choose and click.”
          </p>
          <h2 className="mt-4 text-[42px] font-extrabold leading-[0.95] tracking-[-0.045em] text-[var(--fg)] sm:text-[58px] xl:text-[68px]" data-id="element-399" data-mp="m9bs4fhojpr2">
            Good. Now let’s make that
            <span className="block text-[var(--brand)]" data-id="element-400" data-mp="v7dryrpogccr">data compound.</span>
          </h2>
        </InView>
        <InView delay={0.1} data-id="element-401" data-mp="7h1l7k664lnr">
          <p className="border-l-2 border-[var(--brand)] pl-5 text-[14.5px] leading-[1.7] text-[var(--muted)]" data-id="element-402" data-mp="7g8ay9803e0v">
            <span className="font-semibold text-[var(--fg)]" data-id="element-403" data-mp="22m0wt2nql26q">
              A central overview gives Ferring something individual campaigns cannot
            </span>{' '}
            — while local teams keep control of their campaigns and budgets.
          </p>
          <button type="button" onClick={() => setProofOpen(true)} className="mt-6 inline-flex items-center gap-2.5 rounded-full bg-[var(--fg)] px-6 py-3.5 text-[14px] font-bold text-white shadow-[0_18px_40px_-20px_rgba(14,27,51,0.65)] transition-colors hover:bg-[var(--brand)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2" data-id="element-404" data-mp="2ccccfmgs8ztg">
            <FileBarChartIcon className="h-4 w-4" aria-hidden="true" data-id="element-405" data-mp="1yk5z0850l6xp" />
            Proof case: a Snak data report
          </button>
        </InView>
      </div>

      <div className="mt-12 grid gap-5 lg:grid-cols-2" data-id="element-406" data-mp="34zq16xabup2h">
        {compoundFeatures.map((feature, i) => {
        const Icon = FEATURE_ICONS[feature.key];
        return <InView key={feature.key} delay={0.15 + i * 0.1} y={26} data-id="element-407" data-mp="yaxv43zjoiwl" data-mpi={i}>
              <figure className="h-full rounded-[28px] bg-[#EFEBE2] p-5 sm:p-6" data-id="element-408" data-mp="td4q3e9z9uxw">
                <div className="flex items-center gap-4" data-id="element-409" data-mp="tf8tlv3ysrxn">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px] bg-[var(--brand)] text-white" data-id="element-410" data-mp="okv05y8v4uvm">
                    <Icon className="h-5 w-5" aria-hidden="true" data-id="element-411" data-mp="jl8ovuclhopg" />
                  </span>
                  <div data-id="element-412" data-mp="elvt2k3qx7aq">
                    <span className="block text-[12px] font-extrabold uppercase tracking-[0.2em] text-[var(--fg)]" data-id="element-413" data-mp="ammyjd0vt5e4">
                      {feature.title}
                    </span>
                    <span className="mt-0.5 block text-[13.5px] text-[var(--muted)]" data-id="element-414" data-mp="5p4zceco24jb">{feature.detail}</span>
                  </div>
                </div>
                <div className="mt-5 overflow-hidden rounded-[16px] bg-white p-2 ring-1 ring-[var(--fg)]/[0.08]" data-id="element-415" data-mp="2009zv9sgi0uf">
                  <img src={feature.image} alt={feature.alt} className="block w-full rounded-[10px]" data-id="element-416" data-mp="1v2ro7bn217iq" />
                </div>
              </figure>
            </InView>;
      })}
      </div>

      <ul className="mt-5 grid gap-5 md:grid-cols-3" data-id="element-417" data-mp="wpvsvojdeou8">
        {compoundBenefits.map((benefit, i) => {
        const Icon = BENEFIT_ICONS[benefit.key];
        return <InView key={benefit.key} delay={0.3 + i * 0.06} data-id="element-418" data-mp="noxw9rb9hjlo" data-mpi={i}>
              <li className="flex h-full items-center gap-4 rounded-[22px] bg-white px-5 py-5 ring-1 ring-[var(--fg)]/[0.08]" data-id="element-419" data-mp="90urgl8ygdyg">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--brand)]/10 text-[var(--brand)]" data-id="element-420" data-mp="drtavtp8ps5x">
                  <Icon className="h-[18px] w-[18px]" aria-hidden="true" data-id="element-421" data-mp="1zkh6y36zu3" />
                </span>
                <span data-id="element-422" data-mp="4zhm0c9rycs2">
                  <span className="block text-[12px] font-extrabold uppercase tracking-[0.18em] text-[var(--fg)]" data-id="element-423" data-mp="4wvci73bpn77">
                    {benefit.title}
                  </span>
                  <span className="mt-0.5 block text-[13px] text-[var(--muted)]" data-id="element-424" data-mp="3r5o84mtiyqva">{benefit.detail}</span>
                </span>
              </li>
            </InView>;
      })}
      </ul>

      <InView delay={0.4} data-id="element-425" data-mp="33kvq7xjl3x1b">
        <p className="mt-14 border-t border-[var(--fg)]/10 pt-8 text-[24px] font-extrabold leading-[1.12] tracking-[-0.03em] text-[var(--fg)] sm:text-[30px]" data-id="element-426" data-mp="33lf5t7lri9c6">
          Centralise the visibility, learning and buying power —{' '}
          <span className="text-[var(--brand)]" data-id="element-427" data-mp="3qiql2ha55m0s">not every decision.</span>
        </p>
      </InView>
    </Section>;
}